import { useState, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../app/hooks'
import { sendEmail, resetEmailState } from '../features/emails/emailsSlice'
import {
  selectIsSendingEmail,
  selectIsEmailSuccess,
  selectEmailError,
} from '../features/emails/emailsSelectors'
import { FormData } from '../types/emails.types'

const initialFormData: FormData = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
}

export const useContactForm = () => {
  const dispatch = useAppDispatch()
  const isSending = useAppSelector(selectIsSendingEmail)
  const isSuccess = useAppSelector(selectIsEmailSuccess)
  const error = useAppSelector(selectEmailError)

  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [formErrors, setFormErrors] = useState<Partial<FormData>>({})

  // Reset email state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetEmailState())
    }
  }, [dispatch])

  // Reset form when email is sent successfully
  useEffect(() => {
    if (isSuccess) {
      setFormData(initialFormData)
      setFormErrors({})

      // Reset success state after 5 seconds
      const timeout = setTimeout(() => {
        dispatch(resetEmailState())
      }, 5000)

      return () => clearTimeout(timeout)
    }
  }, [isSuccess, dispatch])

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }))
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }))
    }
  }

  const validateForm = (): boolean => {
    const errors: Partial<FormData> = {}

    if (!formData.name.trim()) {
      errors.name = 'El nombre es requerido'
    }

    if (!formData.email.trim()) {
      errors.email = 'El email es requerido'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email inválido'
    }

    if (!formData.phone.trim()) {
      errors.phone = 'El celular es requerido'
    } else {
      const cleanPhone = formData.phone.replace(/[\s\-()]/g, '')
      // Colombian mobile: 10 digits starting with 3 (e.g., 3001234567)
      // Colombian mobile with +57: 12-13 digits (e.g., +573001234567 or 573001234567)
      // Colombian landline: 10 digits (e.g., 6012345678 for Bogotá)
      const colombianMobileRegex = /^(\+?57)?3\d{9}$/
      const colombianLandlineRegex = /^(\+?57)?[1-8]\d{9}$/

      if (!colombianMobileRegex.test(cleanPhone) && !colombianLandlineRegex.test(cleanPhone)) {
        errors.phone = 'Ingrese un número válido de Colombia (ej: 3001234567 o +573001234567)'
      }
    }

    if (!formData.subject.trim()) {
      errors.subject = 'El asunto es requerido'
    }

    if (!formData.message.trim()) {
      errors.message = 'El mensaje es requerido'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }


    dispatch(
      sendEmail({
        formData_name: formData.name,
        formData_email: formData.email,
        formData_phone: formData.phone,
        formData_subject: formData.subject,
        formData_message: formData.message,
      })
    )
  }

  return {
    formData,
    formErrors,
    isSending,
    isSuccess,
    error,
    handleInputChange,
    handleSubmit,
  }
}